/**
 * O selo de estado que a referência põe no canto de todo cartão.
 *
 * `LIVE`, `PARCIAL`, `HÍBRIDO`. Parece enfeite e não é: é a única coisa na
 * referência que responde, sem ninguém perguntar, a pergunta que mais se faz
 * olhando um painel — **isto aqui está me dizendo a verdade agora?**
 *
 * Por isso ele é derivado do estado real, nunca escrito à mão:
 *
 *  · `vivo`    — a fonte respondeu nesta carga. O dado é de agora.
 *  · `parcial` — parte do que este cartão promete ainda não existe, ou uma das
 *                fontes falhou. O cartão continua útil e diz que está incompleto.
 *  · `frio`    — não há nada ligado aqui.
 *
 * Um selo `LIVE` fixo no HTML seria pior que nenhum: ele afirmaria frescor
 * justamente quando a coluna está velha, que é quando alguém olha.
 */

export type EstadoDoSelo = 'vivo' | 'parcial' | 'frio';

const ROTULO: Readonly<Record<EstadoDoSelo, string>> = {
  vivo: 'LIVE',
  parcial: 'PARCIAL',
  frio: 'FRIO',
};

const EXPLICACAO: Readonly<Record<EstadoDoSelo, string>> = {
  vivo: 'dados desta carga',
  parcial: 'incompleto: parte ainda não existe ou não respondeu',
  frio: 'nada ligado aqui',
};

export function Selo({ estado }: { estado: EstadoDoSelo }): JSX.Element {
  return (
    <span className={`u1-estado u1-estado--${estado}`} title={EXPLICACAO[estado]}>
      <i className="u1-estado__ponto" aria-hidden="true" />
      {ROTULO[estado]}
      {/* O rótulo sozinho é jargão de painel. Para quem usa leitor de tela, o
          que importa é o significado, não a palavra da referência. */}
      <span className="u1-oculto">: {EXPLICACAO[estado]}</span>
    </span>
  );
}

/**
 * O cabeçalho de cartão da referência: título à esquerda, selo à direita.
 *
 * Extraído porque são três cartões hoje e serão mais — e porque a distância
 * entre o título e o selo é o tipo de coisa que diverge sozinha quando cada
 * cartão monta o seu.
 */
export function TopoDoCartao({
  titulo,
  id,
  estado,
}: {
  titulo: string;
  id: string;
  estado: EstadoDoSelo;
}): JSX.Element {
  return (
    <header className="u1-card__topo">
      <h2 className="u1-card__titulo" id={id}>
        {titulo}
      </h2>
      <Selo estado={estado} />
    </header>
  );
}
